import { useEffect, useRef, useState } from "react";

const milestones = [
  {
    year: "2015",
    title: "緣起",
    desc: "創辦人因一次西藏之旅，深受天珠文化感動，開始深入研究東方能量飾品的文化與意涵。",
  },
  {
    year: "2017",
    title: "初心",
    desc: "開始手作第一批天珠手鍊，以最嚴格的標準篩選材質，將文化與美感融入每一件作品。",
  },
  {
    year: "2019",
    title: "擴展",
    desc: "品牌逐漸成形，加入天然水晶礦石、翡翠玉石與唐卡佛牌，建立完整的東方能量選品體系。",
  },
  {
    year: "2022",
    title: "傳承",
    desc: "瓏靖正式以品牌形式對外，致力於讓更多人認識東方能量文化，找到屬於自己的守護力量。",
  },
];

export default function BrandStory() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-[#faf8f4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left: Story Text */}
          <div
            className={`transition-all duration-1000 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#8b6f47]"></span>
              <span
                className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                BRAND STORY
              </span>
            </div>
            <h2
              className="text-[#2d2520] font-bold mb-8 leading-[1.4]"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              品牌故事
            </h2>

            <div
              className="space-y-6 text-[#5a4e45] leading-[1.75]"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              <p>
                瓏靖，取自「瓏」字的玲瓏剔透、「靖」字的安定平和。這兩個字，正是我們對每一件飾品的期許——既有晶瑩剔透的天然美感，也蘊含讓人心靈安定的能量。
              </p>
              <p>
                品牌的起點，是創辦人一次改變人生的西藏之旅。在那片高原上，她第一次親眼見到真正的天珠，感受到那份穿越千年的能量與溫度。回台後，她開始深入研究藏傳文化、礦石能量學與東方美學，並以此為基礎，開始了瓏靖的手作之路。
              </p>
              <p>
                我們相信，每一件飾品都有它的緣分。天珠選擇了它的主人，水晶找到了與它共鳴的靈魂，玉石守護著有緣的人。瓏靖的使命，就是成為這份緣分的橋樑。
              </p>
              <p>
                多年來，我們走訪西藏、雲南、緬甸等地，親自挑選每一顆天珠、每一塊礦石、每一件玉石。不只是商品，更是一份份帶著祝福與守護的禮物，等待與有緣人相遇。
              </p>
            </div>

            {/* Signature */}
            <div className="mt-10 pt-8 border-t border-[#e8e0d5]">
              <p
                className="text-[#8b6f47] text-[1rem] mb-1"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                瓏靖手作能量 創辦人
              </p>
              <p
                className="text-[#2d2520] font-bold"
                style={{
                  fontSize: "1.5rem",
                  fontFamily: "'Noto Serif TC', serif",
                  letterSpacing: "0.1em",
                }}
              >
                陳瓏靖
              </p>
            </div>
          </div>

          {/* Right: Timeline */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            {/* Image */}
            <div className="w-full h-72 rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20asian%20woman%20holding%20Tibetan%20dzi%20bead%20bracelet%20natural%20crystal%20jewelry%20in%20warm%20golden%20light%20studio%20photography%20minimalist%20background%20serene%20peaceful%20expression%20luxury%20brand%20portrait%20high%20quality&width=800&height=500&seq=story001&orientation=landscape"
                alt="品牌創辦人"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-[2.25rem] top-0 bottom-0 w-px bg-[#e8e0d5]"></div>
              <div className="space-y-8">
                {milestones.map((item, i) => (
                  <div
                    key={item.year}
                    className={`flex gap-6 transition-all duration-700`}
                    style={{ transitionDelay: `${i * 100 + 300}ms`, opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(16px)" }}
                  >
                    {/* Year Circle */}
                    <div className="flex-shrink-0 w-[4.5rem] flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-[#8b6f47] flex items-center justify-center z-10">
                        <span className="text-white text-[0.75rem] font-bold">{item.year}</span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="pb-8">
                      <h4
                        className="text-[#2d2520] font-bold mb-2"
                        style={{
                          fontSize: "1.1875rem",
                          fontFamily: "'Noto Serif TC', serif",
                        }}
                      >
                        {item.title}
                      </h4>
                      <p
                        className="text-[#7a6a5a] leading-[1.75]"
                        style={{
                          fontSize: "1.1875rem",
                          fontFamily: "'Noto Serif TC', serif",
                        }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
